import { useState } from 'react';
import { Link } from 'react-router-dom';
import { resolveBattle, resolveBattleSequential } from '@kaeliux/game-engine';
import {
    ALL_SHIP_IDS,
    DEFAULT_SCIENCES,
    SCIENCE_DEFINITIONS,
    UPGRADABLE_SHIP_IDS,
    getShipDefinition,
    type BattleInput,
    type BattleReport,
    type BattleResult,
    type PlayerRace,
    type PlayerSciences,
    type ScienceId,
    type ShipId,
} from '@kaeliux/shared';
import BattleReportView from '../components/BattleReportView';
import { getShipIcon } from '../utils/shipIcons';
import { makeRolls } from '../utils/seededRolls';
import { Button } from '../components/ui/Button';
import { Select, SelectOption } from '../components/ui/Select';
import { Panel } from '../components/ui/Panel';
import { linkButtonClasses } from '../components/ui/buttonStyles';
import { FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES } from '../components/ui/fieldStyles';
import { cn } from '../utils/cn';

/**
 * Battle Simulator (`/fleets/simulator`) — a "what-if" combat calculator
 * reachable from the Fleets page. Players configure both sides' ships, ship
 * upgrade levels, sciences, race and round count, then resolve a battle and read
 * the full round-by-round report (the same `BattleReportView` the real arrival
 * reports use). Nothing is dispatched or persisted — it runs the pure engine
 * client-side.
 *
 * The SEQUENTIAL model is the shipped main battle system (matches what
 * `fleetResolution.ts` uses for real attack/pirate combat).
 *
 * DEV-ONLY extras (gated on `import.meta.env.DEV`, i.e. `yarn dev`/local, hidden
 * in a production build): a reproducible random SEED, and the old SIMULTANEOUS
 * model's report rendered side by side with it — for comparing the two
 * battle-calculation models against each other.
 */

const IS_DEV = import.meta.env.DEV;
const RACES: PlayerRace[] = ['humans', 'robots', 'axons'];
const ROUND_OPTIONS = [3, 5, 8, 12];

/** Only the sciences that measurably change combat (weapon/armor/defence/tactics), with their level caps. */
const COMBAT_SCIENCES: { id: ScienceId; max: number }[] = [
    { id: 'laser', max: 15 },
    { id: 'ion', max: 15 },
    { id: 'plasma', max: 15 },
    { id: 'polymerShields', max: 10 },
    { id: 'carbonFiberShields', max: 10 },
    { id: 'heavyMetalShields', max: 10 },
    { id: 'defenceSystems', max: 20 },
    { id: 'offensiveTactics', max: 10 },
    { id: 'defensiveTactics', max: 10 },
    { id: 'surpriseTactics', max: 10 },
];

const SIM_ATTACKER_ID = 'sim-attacker';
const SIM_DEFENDER_ID = 'sim-defender';
const INLINE_INPUT_CLASS = cn(FIELD_BASE_CLASSES, FIELD_FOCUS_CLASSES, 'px-2 py-1.5 text-sm');

interface SideConfig {
    race: PlayerRace;
    ships: Partial<Record<ShipId, number>>;
    upgrades: Partial<Record<ShipId, number>>;
    sciences: PlayerSciences;
}

function makeSide(race: PlayerRace, ships: Partial<Record<ShipId, number>>): SideConfig {
    return { race, ships, upgrades: {}, sciences: { ...DEFAULT_SCIENCES } };
}

function clampInt(raw: string, min: number, max: number): number {
    const n = Math.floor(Number(raw));
    if (!Number.isFinite(n)) return min;
    return Math.min(max, Math.max(min, n));
}

function toReport(result: BattleResult, attacker: SideConfig, defender: SideConfig): BattleReport {
    return {
        ...result,
        attackerUserId: SIM_ATTACKER_ID,
        defenderUserId: SIM_DEFENDER_ID,
        attackerRace: attacker.race,
        defenderRace: defender.race,
        stolenResources: { metal: 0, minerals: 0, gas: 0, scrap: 0 },
        occurredAt: new Date(),
    };
}

function SideForm({ title, config, onChange }: { title: string; config: SideConfig; onChange: (next: SideConfig) => void }) {
    const [showSciences, setShowSciences] = useState(false);
    const totalShips = Object.values(config.ships).reduce((sum, n) => sum + (n ?? 0), 0);
    const totalPopulation = (Object.entries(config.ships) as [string, number][])
        .reduce((sum, [id, n]) => sum + (n ?? 0) * getShipDefinition(config.race, Number(id) as ShipId).hangar, 0);

    function maxSciences() {
        const next = { ...config.sciences };
        for (const { id, max } of COMBAT_SCIENCES) next[id] = max;
        onChange({ ...config, sciences: next });
    }

    return (
        <Panel>
            <div className="flex items-center justify-between">
                <h3 className="text-lg font-bold text-content-primary">{title}</h3>
                <label className="flex items-center gap-2 text-sm text-content-secondary">
                    Race
                    <Select
                        value={config.race}
                        onChange={(e) => onChange({ ...config, race: e.target.value as PlayerRace })}
                        data-testid={`race-${title.toLowerCase()}`}
                    >
                        {RACES.map((r) => <SelectOption key={r} value={r}>{r}</SelectOption>)}
                    </Select>
                </label>
            </div>

            <div className="flex flex-col gap-1">
                <div className="grid grid-cols-[1fr_auto_auto] gap-2 text-xs text-content-muted font-semibold uppercase tracking-wide px-1">
                    <span>Ship ({totalShips.toLocaleString()} total · {totalPopulation.toLocaleString()} pop)</span>
                    <span className="w-24 text-right">Count</span>
                    <span className="w-16 text-right">Upgrade</span>
                </div>
                {ALL_SHIP_IDS.map((id) => {
                    const def = getShipDefinition(config.race, id);
                    const upgradable = UPGRADABLE_SHIP_IDS.includes(id);
                    return (
                        <div key={id} className="grid grid-cols-[1fr_auto_auto] gap-2 items-center">
                            <span className="flex items-center gap-1.5 text-sm text-content-primary min-w-0">
                                <img src={getShipIcon(config.race, id)} alt={def.name} className="w-8 h-8 object-contain shrink-0" />
                                <span className="truncate">{def.name}</span>
                            </span>
                            <input
                                type="number"
                                min={0}
                                value={config.ships[id] ?? 0}
                                onChange={(e) => onChange({ ...config, ships: { ...config.ships, [id]: clampInt(e.target.value, 0, 10_000_000) } })}
                                className={`${INLINE_INPUT_CLASS} w-24 text-right`}
                                data-testid={`ship-${title.toLowerCase()}-${id}`}
                            />
                            <input
                                type="number"
                                min={0}
                                max={10}
                                disabled={!upgradable}
                                value={config.upgrades[id] ?? 0}
                                onChange={(e) => onChange({ ...config, upgrades: { ...config.upgrades, [id]: clampInt(e.target.value, 0, 10) } })}
                                className={`${INLINE_INPUT_CLASS} w-16 text-right`}
                                data-testid={`upgrade-${title.toLowerCase()}-${id}`}
                            />
                        </div>
                    );
                })}
            </div>

            <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={() => setShowSciences((s) => !s)}
                className="text-left justify-start"
                data-testid={`toggle-sciences-${title.toLowerCase()}`}
            >
                {showSciences ? '▾' : '▸'} Sciences
            </Button>
            {showSciences && (
                <div className="flex flex-col gap-2">
                    <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={maxSciences}
                        className="self-start"
                        data-testid={`max-sciences-${title.toLowerCase()}`}
                    >
                        Max all sciences
                    </Button>
                    <div className="grid grid-cols-2 gap-2">
                        {COMBAT_SCIENCES.map(({ id, max }) => (
                            <label key={id} className="flex items-center justify-between gap-2 text-xs text-content-secondary">
                                <span className="truncate">{SCIENCE_DEFINITIONS[id].name}</span>
                                <input
                                    type="number"
                                    min={0}
                                    max={max}
                                    value={config.sciences[id]}
                                    onChange={(e) => onChange({ ...config, sciences: { ...config.sciences, [id]: clampInt(e.target.value, 0, max) } })}
                                    className={`${INLINE_INPUT_CLASS} w-14 text-right`}
                                    data-testid={`science-${title.toLowerCase()}-${id}`}
                                />
                            </label>
                        ))}
                    </div>
                </div>
            )}
        </Panel>
    );
}

interface SimResult {
    sequential: BattleReport;
    simultaneous: BattleReport | null;
}

export default function BattleSimulatorPage() {
    const [attacker, setAttacker] = useState<SideConfig>(() => makeSide('humans', { 3: 100 }));
    const [defender, setDefender] = useState<SideConfig>(() => makeSide('robots', { 3: 80 }));
    const [rounds, setRounds] = useState(8);
    const [seed, setSeed] = useState('');
    const [result, setResult] = useState<SimResult | null>(null);

    function simulate() {
        const input: BattleInput = {
            attacker: { race: attacker.race, ships: attacker.ships, shipUpgrades: attacker.upgrades, sciences: attacker.sciences },
            defender: { race: defender.race, ships: defender.ships, shipUpgrades: defender.upgrades, sciences: defender.sciences },
            rounds,
        };
        // Over-provision rolls; both engines treat an exhausted array as "no more
        // triggers". The SAME array feeds both models so the only difference is
        // the model, not the dice.
        const rolls = makeRolls((rounds + 1) * 4_000, IS_DEV && seed.trim() !== '' ? Number(seed) : undefined);
        setResult({
            sequential: toReport(resolveBattleSequential(input, rolls), attacker, defender),
            simultaneous: IS_DEV ? toReport(resolveBattle(input, rolls), attacker, defender) : null,
        });
    }

    return (
        <main className="max-w-5xl mx-auto p-4 flex flex-col gap-4">
            <div className="flex items-center justify-between flex-wrap gap-2">
                <h1 className="text-title font-bold text-content-primary">Battle Simulator</h1>
                <Link to="/fleets" className={linkButtonClasses('secondary', 'sm')}>← Back to Fleets</Link>
            </div>
            <p className="text-content-muted text-sm">
                Configure both sides and resolve a hypothetical battle. Nothing is sent or saved.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <SideForm title="Attacker" config={attacker} onChange={setAttacker} />
                <SideForm title="Defender" config={defender} onChange={setDefender} />
            </div>

            <Panel className="flex flex-wrap items-end gap-4">
                <label className="flex flex-col gap-1 text-sm text-content-secondary">
                    Rounds
                    <Select value={rounds} onChange={(e) => setRounds(Number(e.target.value))} data-testid="sim-rounds">
                        {ROUND_OPTIONS.map((r) => <SelectOption key={r} value={r}>{r}</SelectOption>)}
                    </Select>
                </label>
                {IS_DEV && (
                    <label className="flex flex-col gap-1 text-sm text-content-secondary">
                        Seed <span className="text-content-muted text-xs">(dev — blank = random)</span>
                        <input
                            type="number"
                            value={seed}
                            onChange={(e) => setSeed(e.target.value)}
                            placeholder="random"
                            className={`${INLINE_INPUT_CLASS} w-32`}
                            data-testid="sim-seed"
                        />
                    </label>
                )}
                <Button type="button" variant="primary" size="md" onClick={simulate} data-testid="simulate-button">
                    Simulate
                </Button>
            </Panel>

            {result && (
                <div className={`grid grid-cols-1 gap-4 ${result.simultaneous ? 'xl:grid-cols-2' : ''}`}>
                    <Panel data-testid="report-sequential">
                        {(result.simultaneous && result.sequential) && (
                            <h2 className="text-lg font-bold text-content-primary">
                                Sequential
                            </h2>
                        )}
                        <BattleReportView report={result.sequential} viewerUserId={SIM_ATTACKER_ID} collapseAttacks />
                    </Panel>
                    {result.simultaneous && (
                        <Panel data-testid="report-simultaneous">
                            <h2 className="text-lg font-bold text-content-primary">
                                Simultaneous <span className="text-status-warning text-xs font-normal">— dev</span>
                            </h2>
                            <BattleReportView report={result.simultaneous} viewerUserId={SIM_ATTACKER_ID} />
                        </Panel>
                    )}
                </div>
            )}
        </main>
    );
}
